import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  CircularProgressbar,
  buildStyles
} from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { Dropdown, DropdownButton } from 'react-bootstrap';
import { displayConvertedAmount } from '../../utils/currencyConverter';

const SalesDashboard = () => {
  const [sale, setSale] = useState({});
  const [selectedFilter, setSelectedFilter] = useState("financialYear");
  const [isDateFilterVisible, setIsDateFilterVisible] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [salesUsers, setSalesUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userData, setUserData] = useState(null);
  const [reminders, setReminders] = useState([]);

  const COLORS = ['#559dfc', '#74affd', '#91c0fd', '#add2fe', '#cae3fe'];

  const fetchSale = async (startDate, endDate, userId = selectedUserId) => {
    setIsLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const userInfo = JSON.parse(localStorage.getItem('user-info'));
      const origin = localStorage.getItem('user-origin');
      const token = userInfo?.token;

      const response = await axios.get(`${backendUrl}/api/dashboard/sale`, {
        params: {
          start_date: startDate,
          end_date: endDate,
          created_by: userId || undefined
        },
        headers: {
          Authorization: `Bearer ${token}`,
          'Tenant-Origin': origin,
        },
      });

      setSale(response.data);
    } catch (error) {
      console.error("Error fetching data:", error.response?.data || error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReminders = async (userId = selectedUserId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const userInfo = JSON.parse(localStorage.getItem('user-info'));
      const origin = localStorage.getItem('user-origin');
      const token = userInfo?.token;

      const response = await axios.get(`${backendUrl}/api/activities/reminders`, {
        params: { user_id: userId || undefined },
        headers: {
          Authorization: `Bearer ${token}`,
          'Tenant-Origin': origin,
        },
      });
      setReminders(response.data.data);
    } catch (error) {
      console.error("Error fetching reminders:", error);
    }
  };

  const fetchSalesUsers = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const userInfo = JSON.parse(localStorage.getItem('user-info'));
      const origin = localStorage.getItem('user-origin');
      const token = userInfo?.token;

      const response = await axios.get(`${backendUrl}/api/users`, {
        params: { role: 'Inside Sales' }, // Changed to 'Inside Sales' as per requirement
        headers: {
          Authorization: `Bearer ${token}`,
          'Tenant-Origin': origin,
        },
      });
      setSalesUsers(response.data.users || []);
    } catch (error) {
      console.error("Error fetching sales users:", error);
    }
  };

  const handleFinancialYearChange = (selectedYear) => {
    if (selectedYear === "financialYear") {
      setSelectedFilter("financialYear");
      setIsDateFilterVisible(false);
      const { startDate, endDate } = getFinancialYear();
      fetchSale(startDate, endDate);
    } else {
      setSelectedFilter("customRange");
      setIsDateFilterVisible(true);
      setCustomDateRange({ startDate: '', endDate: '' });
    }
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setCustomDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClearDates = () => {
    setCustomDateRange({ startDate: '', endDate: '' });
    setIsDateFilterVisible(false);
    fetchSale(null, null);
  };

  const getFinancialYear = () => {
    const monthMap = {
      January: '01', February: '02', March: '03', April: '04',
      May: '05', June: '06', July: '07', August: '08',
      September: '09', October: '10', November: '11', December: '12'
    };

    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonthNum = today.getMonth() + 1;

    try {
      const storedFormData = JSON.parse(localStorage.getItem('formData'));
      
      // 1. Try to get explicit months from settings
      let sMonthName = storedFormData?.start_month;
      let eMonthName = storedFormData?.end_month;

      // 2. Fallback to parsing the financial_year string if months aren't set
      if (!sMonthName || !eMonthName) {
        const fy = storedFormData?.financial_year;
        if (fy && fy.includes('-')) {
          const parts = fy.split('-').map(m => m.trim());
          if (isNaN(parseInt(parts[0]))) {
            sMonthName = parts[0];
            eMonthName = parts[1];
          } else {
            // Case: "2024-2025"
            return {
              startDate: `${parts[0]}-04-01`,
              endDate: `${parts[1]}-03-31`
            };
          }
        }
      }

      if (sMonthName && eMonthName) {
        const sMonth = monthMap[sMonthName];
        const eMonth = monthMap[eMonthName];

        if (sMonth && eMonth) {
          const sMonthInt = parseInt(sMonth);
          // If current month is before start month, started last year
          const startYear = currentMonthNum < sMonthInt ? currentYear - 1 : currentYear;
          const endYear = parseInt(eMonth) < sMonthInt ? startYear + 1 : startYear;
          const endDay = new Date(endYear, parseInt(eMonth), 0).getDate();

          return {
            startDate: `${startYear}-${sMonth}-01`,
            endDate: `${endYear}-${eMonth}-${endDay}`
          };
        }
      }
    } catch (e) {
      console.error("Failed to parse financial year, using fallback:", e);
    }

    // Default Fallback
    return {
      startDate: `${currentYear}-04-01`,
      endDate: `${currentYear + 1}-03-31`
    };
  };

  const handleSubmit = () => {
    const { startDate, endDate } = customDateRange;
    if (startDate && endDate) {
      fetchSale(startDate, endDate);
      setIsDateFilterVisible(false);
    }
  };

  useEffect(() => {
    const userInfo = JSON.parse(localStorage.getItem('user-info'));
    setUserData(userInfo);

    const hasAllScope = userInfo?.user?.roles?.some(r => r.data_scope === 'all' || r.name === 'superadmin');
    if (hasAllScope) {
      fetchSalesUsers();
    }
    fetchSale();
    fetchReminders();
  }, []);

  const handleUserChange = (userId) => {
    setSelectedUserId(userId);
    const { startDate, endDate } = customDateRange;
    fetchSale(startDate || null, endDate || null, userId);
    fetchReminders(userId);
  };

  const formatCurrency = (value) => {
    return displayConvertedAmount(value, "AED");
  };

  const formatUSCurrency = (amount) => {
    return displayConvertedAmount(amount, "AED");
  };

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0 font-size-18">
                  Dashboard
                </h4>
              </div>
            </div>
          </div>

          <div className="dashboard-container">
            <div>
              <div>
                <div className="d-flex justify-content-end align-items-center mb-3">

                  {userData?.user?.roles?.some(r => r.data_scope === 'all' || r.name === 'superadmin') && (
                    <div className="me-3">
                      <select
                        className="form-select"
                        value={selectedUserId}
                        onChange={(e) => handleUserChange(e.target.value)}
                        style={{ width: '200px' }}
                      >
                        <option value="">All Sales Users</option>
                        {salesUsers.map(user => (
                          <option key={user.id} value={user.id}>{user.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <DropdownButton
                    id="financial-year-dropdown"
                    title={selectedFilter === "financialYear" ? 'This Financial Year' : 'Custom Range'}
                    onSelect={handleFinancialYearChange}
                    variant="outline-primary"
                    className="filter-dropdown"
                  >
                    <Dropdown.Item eventKey="financialYear">This Financial Year</Dropdown.Item>
                    <Dropdown.Item eventKey="custom">Custom Range</Dropdown.Item>
                  </DropdownButton>

                  {isDateFilterVisible && (
                    <div className="date-range-selector">
                      <input
                        type="month"
                        name="startDate"
                        value={customDateRange.startDate}
                        onChange={handleDateChange}
                        className="form-control date-input"
                      />
                      <span className="date-separator">to</span>
                      <input
                        type="month"
                        name="endDate"
                        value={customDateRange.endDate}
                        onChange={handleDateChange}
                        className="form-control date-input"
                      />
                      <button
                        onClick={handleClearDates}
                        className="btn btn-outline-secondary"
                      >
                        Clear
                      </button>
                      <button
                        onClick={handleSubmit}
                        className="btn btn-primary"
                        disabled={!customDateRange.startDate || !customDateRange.endDate}
                      >
                        Apply
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <>
              {/* Key Metrics Section */}
              {/* <div className="metrics-grid1">
            <div className="metric-card1 primary">
              <div className="metric-content1">
                <h3>Total Sales</h3>
                <p className='font-size-18'>{sale.sales?.total}</p>
              </div>
              <div className="metric-icon">
                <i className="bx bx-trending-up"></i>
              </div>
            </div>

            <div className="metric-card1 success">
              <div className="metric-content1">
                <h3>Target Progress</h3>
                <div className="progress-circle">
                  <CircularProgressbar
                    value={sale.sales?.target_progress || 0}
                    text={`${sale.sales?.target_progress || 0}%`}
                    strokeWidth={10}
                    styles={buildStyles({
                      textSize: "16px",
                      pathColor: "#10B981",
                      textColor: "#111827",
                      trailColor: "#E5E7EB",
                    })}
                  />
                </div>
              </div>
            </div>

            <div className="metric-card1 info">
              <div className="metric-content1">
                <h3>Conversion Rate</h3>
                <p>{sale.sales?.conversion_rate || 0}%</p>
              </div>
              <div className="metric-icon">
                <i className="bx bx-transfer"></i>
              </div>
            </div>

            <div className="metric-card1 warning">
              <div className="metric-content1">
                <h3>Avg Order Value</h3>
                <p>{sale.sales?.avg_order_value}</p>
              </div>
              <div className="metric-icon">
                <i className="bx bx-cart"></i>
              </div>
            </div>
          </div> */}
              <div className="metrics-grid3">
                {/* Total Sales Card */}
                <div className="metric-card3 sales-card">
                  <div className="card-content">
                    <div className="metric-info">
                      <span className="metric-label">Total Sales</span>
                      <span className="metric-value">{formatUSCurrency(sale.sales?.total)}</span>
                    </div>
                    <div className="metric-icon">
                      <i className="bx bx-trending-up"></i>
                    </div>
                  </div>
                  <div className="metric-footer">
                    <span className="trend positive">
                      <i className="bx bx-up-arrow-alt"></i> 12.5% from last period
                    </span>
                  </div>
                </div>

                {/* Target Progress Card */}
                <div className="metric-card3 target-card">
                  <div className="card-content">
                    <div className="metric-info">
                      <span className="metric-label">Target Progress</span>
                      <div className="progress-container">
                        <CircularProgressbar
                          value={sale.sales?.target_progress || 0}
                          text={`${sale.sales?.target_progress || 0}%`}
                          strokeWidth={12}
                          styles={buildStyles({
                            pathColor: "#4CAF50",
                            textColor: "#2E7D32",
                            trailColor: "#E8F5E9",
                            textSize: '28px'
                          })}
                        />
                      </div>
                    </div>
                    <div className="metric-icon">
                      <i className="bx bx-target-lock"></i>
                    </div>
                  </div>
                </div>

                {/* Conversion Rate Card */}
                <div className="metric-card3 conversion-card">
                  <div className="card-content">
                    <div className="metric-info">
                      <span className="metric-label">Conversion Rate</span>
                      <span className="metric-value">{sale.sales?.conversion_rate || 0}%</span>
                    </div>
                    <div className="metric-icon">
                      <i className="bx bx-transfer"></i>
                    </div>
                  </div>
                  <div className="metric-footer">
                    <span className="trend positive">
                      <i className="bx bx-up-arrow-alt"></i> 3.2% from last month
                    </span>
                  </div>
                </div>

                {/* Average Order Value Card */}
                <div className="metric-card3 aov-card">
                  <div className="card-content">
                    <div className="metric-info">
                      <span className="metric-label">Avg Order Value</span>
                      <span className="metric-value">{formatUSCurrency(sale.sales?.avg_order_value)}</span>
                    </div>
                    <div className="metric-icon">
                      <i className="bx bx-cart"></i>
                    </div>
                  </div>
                  <div className="metric-footer">
                    <span className="trend neutral">
                      <i className="bx bx-minus"></i> 0.5% from last month
                    </span>
                  </div>
                </div>
              </div>

              {/* Activity Reminders Section */}
              {reminders.length > 0 && (
                <div className="dashboard-section mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Upcoming Activity Reminders</h5>
                    <span className="badge bg-soft-primary text-primary">{reminders.length} Pending</span>
                  </div>
                  <div className="row">
                    {reminders.map((reminder) => (
                      <div key={reminder.id} className="col-md-4 mb-3">
                        <div className="card h-100 shadow-sm border-0" style={{ borderRadius: '15px', borderLeft: `5px solid ${reminder.activity_type?.color || '#559dfc'}` }}>
                          <div className="card-body p-3">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <span className="badge" style={{ backgroundColor: `${reminder.activity_type?.color}20`, color: reminder.activity_type?.color || '#559dfc', fontSize: '11px' }}>
                                {reminder.activity_type?.name || reminder.activity_type || 'Activity'}
                              </span>
                              <small className="text-muted">
                                <i className="bx bx-calendar me-1"></i>
                                {new Date(reminder.due_date).toLocaleDateString()}
                              </small>
                            </div>
                            <h6 className="card-title mb-1 text-truncate" title={reminder.subject}>{reminder.subject}</h6>
                            <p className="card-text text-muted small mb-2 text-truncate-2" style={{ height: '32px', overflow: 'hidden' }}>
                              {reminder.notes || 'No description provided'}
                            </p>
                            <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                              <span className={`badge ${reminder.priority === 'high' ? 'bg-danger' : reminder.priority === 'low' ? 'bg-info' : 'bg-warning'}`} style={{ fontSize: '10px' }}>
                                {reminder.priority?.toUpperCase()}
                              </span>
                              {reminder.reminder_at && (
                                <small className="text-danger fw-bold">
                                  <i className="bx bx-alarm me-1"></i>
                                  {new Date(reminder.reminder_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </small>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sales Breakdown Section */}
              <div className="dashboard-section">
                <h5>Sales Breakdown</h5>
                <div className="breakdown-grid">
                  <div className="breakdown-card">
                    <h4>By Product</h4>
                    {sale?.sales?.by_product?.length ? (
                      <div className="chart-container">
                        <ResponsiveContainer width="100%" height={100}>
                          <BarChart
                            data={sale.sales.by_product
                              .map(item => ({ ...item, sales: parseFloat(item.sales) }))
                              .sort((a, b) => b.sales - a.sales)}
                            layout="vertical"
                            margin={{ top: 5, right: 10, bottom: 5, left: 30 }}
                          >
                            <XAxis type="number" hide />
                            <YAxis type="category" dataKey="product" width={100} hide />
                            <Tooltip
                              formatter={(value) => [`₹ ${value.toLocaleString()}`, 'Sales']}
                            />
                            <Bar
                              dataKey="sales"
                              radius={[0, 4, 4, 0]}
                              fill="#8bbafc"
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="no-data">No product sales data available</div>
                    )}
                  </div>

                  <div className="breakdown-card">
                    <h4>By Region</h4>
                    {sale?.sales?.by_region?.length ? (
                      <div className="chart-container">
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie
                              data={sale.sales.by_region}
                              dataKey="sales"
                              nameKey="region"
                              outerRadius={80}
                              label={false}
                              stroke="none"
                            >
                              {sale.sales.by_region.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(value, name, props) => {
                                const region = props?.payload?.region; // Get region name from payload
                                return [`${region}  ${value.toLocaleString()}`];
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="no-data">No regional sales data available</div>
                    )}
                  </div>

                </div>
              </div>

              {/* Additional Metrics Section */}
              <div className="dashboard-section">
                <h5>Performance Metrics</h5>
                <div className="metrics-grid1 secondary">
                  <div className="metric-card1">
                    <h6>New Customers</h6>
                    <p className="large-text font-size-18">{sale.sales?.new_customers || 0}</p>
                    <div className="metric-footer">
                      <span className="trend up">
                        <i className="bx bx-up-arrow-alt"></i> 12%
                      </span>
                      <span>vs last period</span>
                    </div>
                  </div>

                  <div className="metric-card1">
                    <h6>Pending Quotations</h6>
                    <p className="large-text font-size-18">{sale.sales?.pending_documents || 0}</p>
                    <div className="metric-footer">
                      <span className="trend down">
                        <i className="bx bx-down-arrow-alt"></i> 5%
                      </span>
                      <span>vs last period</span>
                    </div>
                  </div>

                  <div className="metric-card1">
                    <h6>Pending Invoices</h6>
                    <p className="large-text font-size-18">{sale.sales?.pending_invoices || 0}</p>
                    <div className="metric-footer">
                      <span className="trend up">
                        <i className="bx bx-up-arrow-alt"></i> 8%
                      </span>
                      <span>vs last period</span>
                    </div>
                  </div>

                  <div className="metric-card1">
                    <h6>Top Salesperson</h6>
                    <p className="large-text font-size-18">
                      {sale.sales?.top_salesperson?.name || ''}
                    </p>
                    <div className="metric-footer">
                      <span>{sale.sales?.top_salesperson?.amount}</span>
                    </div>
                  </div>
                </div>
              </div>
            </>


          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesDashboard;
